import { StandardEvents } from '@shopify/events';

const MINIMUM_ORDER = 10;

/**
 * @param {number|string} itemCount
 * @returns {{count: number, reached: boolean, remaining: number, thresholdPosition: number}}
 */
export function getMinimumOrderState(itemCount) {
  const parsedCount = Number(itemCount);
  const count = Number.isFinite(parsedCount) ? Math.max(0, parsedCount) : 0;
  return {
    count,
    reached: count >= MINIMUM_ORDER,
    remaining: Math.max(MINIMUM_ORDER - count, 0),
    thresholdPosition: Math.min((count / MINIMUM_ORDER) * 72, 72),
  };
}

class B2BMinimumOrder extends HTMLElement {
  static observedAttributes = ['data-cart-count'];

  #root = null;

  connectedCallback() {
    if (this.#root) return;
    this.#root = this.closest('cart-items-component') || this.parentElement || document;
    this.#root.addEventListener('click', this.#blockBelowMinimum, true);
    this.#root.addEventListener('submit', this.#blockBelowMinimum, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#syncCheckoutState();
  }

  disconnectedCallback() {
    this.#root?.removeEventListener('click', this.#blockBelowMinimum, true);
    this.#root?.removeEventListener('submit', this.#blockBelowMinimum, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#root = null;
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#syncCheckoutState();
  }

  #handleCartUpdate = (event) => {
    const syncAfterRender = () => queueMicrotask(() => this.#syncCheckoutState());
    if (event.promise instanceof Promise) {
      event.promise.then(syncAfterRender).catch(() => {});
    } else {
      syncAfterRender();
    }
  };

  #state() {
    return getMinimumOrderState(this.dataset.cartCount);
  }

  #syncCheckoutState() {
    const { count, reached, remaining, thresholdPosition } = this.#state();
    const checkout = this.#root?.querySelector('[data-b2b-checkout]');
    const accelerated = this.#root?.querySelector('[data-b2b-accelerated-checkout]');
    const countLabel = this.querySelector('[data-b2b-minimum-count]');
    const message = this.querySelector('[data-b2b-minimum-message]');
    const node = this.querySelector('[data-b2b-unlock-node]');
    const fill = this.querySelector('[data-b2b-minimum-fill]');

    this.dataset.unlocked = String(reached);
    this.style.setProperty('--b2b-threshold-position', `${thresholdPosition}%`);
    if (countLabel instanceof HTMLElement) countLabel.textContent = `${count} pieces`;
    if (message instanceof HTMLElement) {
      message.textContent = reached ? '✓ Checkout unlocked' : `${remaining} more to checkout`;
    }
    if (node instanceof HTMLElement) node.textContent = reached ? '✓' : '10+';
    node?.toggleAttribute('data-unlocked', reached);
    fill?.toggleAttribute('data-unlocked', reached);

    if (checkout instanceof HTMLButtonElement) {
      checkout.disabled = !reached;
      checkout.toggleAttribute('aria-disabled', !reached);
    }
    if (accelerated instanceof HTMLElement) {
      accelerated.hidden = !reached;
    }
  }

  #blockBelowMinimum = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutAttempt =
      (event.type === 'click' && target.closest('[data-b2b-checkout], [data-b2b-accelerated-checkout]')) ||
      (event.type === 'submit' && target.matches('#cart-form'));
    if (!checkoutAttempt || this.#state().reached) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.querySelector('[data-b2b-minimum-status]')?.focus();
  };
}

if (!customElements.get('b2b-minimum-order')) {
  customElements.define('b2b-minimum-order', B2BMinimumOrder);
}
